import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { Box, Button } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import PageBlock from '@sharedCR/common/PageBlock';
import { AddBoxIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals/ModalCDS/ModalCDS.constants';
import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoansPaths } from 'app/configs/routesConfig/loans';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import dayjs from 'dayjs';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { IconSize } from 'constants/coreDesignSystem';
import { COMMON_REQUEST_DATE_FORMAT } from 'constants/date';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import usePendingChangeAuthors from 'hooks/usePendingChangeAuthors';
import usePendingChangesDependentFetch from 'hooks/usePendingChangesDependentFetch';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';
import { logger } from 'utils/logger';

import { getInvestorsAction } from 'store/accounting/actions';
import { selectInvestorsOptions } from 'store/accounting/selectors';
import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openDialogAction, showMessageAction } from 'store/global/actions';
import {
  editPostClosingAction,
  getLoanFeesSummary,
  getPostClosingAction,
  getPropertyAndLoanSummaryAction,
} from 'store/loanApplications/actions';
import {
  selectClosingDocumentRecords,
  selectEntityChangedPropsById,
  selectExtendedImpounds,
  selectIsUnsecuredFromPropertyAndLoanSummary,
  selectLoanFeesSummary,
  selectPostClosing,
  selectPropertyAndLoanSummarySubjectPropertyData,
} from 'store/loanApplications/selectors';
import { getPostClosingOptionsAction } from 'store/system/actions';
import { selectPostClosingOptionsByListType } from 'store/system/selectors';
import { getUsersAction } from 'store/users/actions';
import { selectExtendedUsers } from 'store/users/selectors';

import DocumentRecordActions from './DocumentRecordActions';
import DocumentRecordModal from './DocumentRecordModal';
import { EditDrawer } from './EditDrawer';
import { SECTION_HREF_MAP } from './EditDrawer/EditDrawer.constants';
import FinalDocumentsSection from './FinalDocumentsSection';
import FinancialDetails from './FinancialDetails';
import { InvestorConditionsTable } from './InvestorConditions';
import InvestorServicerSummary from './InvestorServicerSummary';
import {
  getAccountingGridConfig,
  getDocumentsTableConfig,
  getFinalDocumentsGridConfig,
  getFlippedUnsalableGridConfig,
  getInvestorSummaryConfig,
  getLoanIdentifiersConfig,
  getMersGridConfig,
  getOverviewConfig,
  getPaymentGridConfig,
  getServicerSummaryConfig,
  getShipperMilestoneGridConfig,
  getShippingGridConfig,
} from './PostClosing.config';
import { addDocumentButtonWrapperStyles, wrapperStyles } from './PostClosing.styles';
import { PostClosingTestIds } from './PostClosing.testIds';
import { buildPostClosingPayload } from './PostClosing.utils';
import PostClosingOverview from './PostClosingOverview';
import { PreInvestorConditionsTable } from './PreInvestorConditions';
import ShipperMilestoneSection from './ShipperMilestoneSection';

const PostClosing = ({ isLoading, setIsLoading }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { applicationId, tab } = useParams();
  const loanId = useLoanId();

  const [isError, setIsError] = useState(false);
  const isInitialLoadRef = useRef(true);

  const pendingChangesData = useSelector(selectEntityChangedPropsById(loanId));
  const fieldAuthors = usePendingChangeAuthors(loanId);
  const postClosing = useSelector(selectPostClosing);
  const documentRecords = useSelector(selectClosingDocumentRecords);
  const investorsOptions = useSelector(selectInvestorsOptions);
  const subjectPropertyData = useSelector(selectPropertyAndLoanSummarySubjectPropertyData);
  const impoundsData = useSelector(selectExtendedImpounds);
  const isUnsecured = useSelector(selectIsUnsecuredFromPropertyAndLoanSummary);
  const loanFeesSummary = useSelector(selectLoanFeesSummary);
  const extendedUsers = useSelector(selectExtendedUsers);
  const optionsByListType = useSelector(selectPostClosingOptionsByListType);

  const { isViewAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_POST_CLOSING),
  );

  const summaryPopover = usePopover();
  const financialPopover = usePopover();
  const finalDocumentsPopover = usePopover();
  const shipperMilestonePopover = usePopover();
  const mersPopover = usePopover();
  const flippedUnsalablePopover = usePopover();

  const fetchData = useCallback(() => {
    setIsError(false);

    if (isInitialLoadRef.current) {
      setIsLoading(true);
    }

    Promise.all([
      ...getConditionalArray(loanId, () => [dispatch(getPostClosingAction({ loanId }))]),
      ...getConditionalArray(applicationId, () => [
        dispatch(getPropertyAndLoanSummaryAction({ applicationId })),
        dispatch(getLoanFeesSummary({ applicationId })),
      ]),
      dispatch(getInvestorsAction()),
      dispatch(getUsersAction({ includeDeleted: true })),
    ])
      .catch(() => setIsError(true))
      .finally(() => {
        setIsLoading(false);
        isInitialLoadRef.current = false;
      });
  }, [dispatch, loanId, applicationId, setIsLoading]);

  usePendingChangesDependentFetch(fetchData);

  useEffectSafeStrictMode(() => {
    dispatch(getPostClosingOptionsAction());
  }, [dispatch]);

  const investorName = useMemo(() => {
    return investorsOptions.find(({ value }) => value === postClosing?.investorId)?.name;
  }, [investorsOptions, postClosing?.investorId]);

  const monthlyEscrow = useMemo(() => {
    const impounds = impoundsData?.impounds ?? [];

    if (!impounds.length) {
      return null;
    }

    return impounds.reduce((sum, impound) => sum + (impound.monthlyAmount ?? 0), 0);
  }, [impoundsData?.impounds]);

  const overviewConfig = useMemo(
    () =>
      getOverviewConfig({
        postClosing,
        pendingChangesData,
        fieldAuthors,
        principalAndInterest: subjectPropertyData?.principalAndInterest,
        monthlyEscrow,
        isUnsecured,
      }),
    [
      postClosing,
      pendingChangesData,
      fieldAuthors,
      subjectPropertyData?.principalAndInterest,
      monthlyEscrow,
      isUnsecured,
    ],
  );

  const investorSummaryConfig = useMemo(
    () =>
      getInvestorSummaryConfig({
        postClosing,
        investorName,
        pendingChangesData,
        fieldAuthors,
        isUnsecured,
      }),
    [postClosing, investorName, pendingChangesData, fieldAuthors, isUnsecured],
  );

  const servicerSummaryConfig = useMemo(
    () => getServicerSummaryConfig({ postClosing, pendingChangesData, fieldAuthors }),
    [postClosing, pendingChangesData, fieldAuthors],
  );

  const loanIdentifiersConfig = useMemo(
    () => getLoanIdentifiersConfig({ postClosing, pendingChangesData, fieldAuthors, isUnsecured }),
    [postClosing, pendingChangesData, fieldAuthors, isUnsecured],
  );

  const getUserName = useCallback(userId => extendedUsers[userId]?.name ?? null, [extendedUsers]);

  const { accountingGridConfig, shippingGridConfig } = useMemo(
    () => ({
      accountingGridConfig: getAccountingGridConfig({
        postClosing,
        pendingChangesData,
        fieldAuthors,
        isUnsecured,
      }),
      shippingGridConfig: isUnsecured
        ? null
        : getShippingGridConfig({ postClosing, pendingChangesData, fieldAuthors, getUserName }),
    }),
    [postClosing, pendingChangesData, fieldAuthors, isUnsecured, getUserName],
  );

  const paymentGridConfig = useMemo(
    () => getPaymentGridConfig({ postClosing, pendingChangesData, fieldAuthors }),
    [postClosing, pendingChangesData, fieldAuthors],
  );

  const finalDocumentsGridConfig = useMemo(
    () =>
      getFinalDocumentsGridConfig({
        postClosing,
        getUserName,
        closingDate: loanFeesSummary?.closingDate,
        firstPaymentDate: loanFeesSummary?.firstPaymentDate,
      }),
    [postClosing, getUserName, loanFeesSummary?.closingDate, loanFeesSummary?.firstPaymentDate],
  );

  const shipperMilestoneGridConfig = useMemo(
    () => getShipperMilestoneGridConfig({ postClosing, getUserName }),
    [postClosing, getUserName],
  );

  const mersGridConfig = useMemo(
    () => getMersGridConfig({ postClosing, getUserName, optionsByListType }),
    [postClosing, getUserName, optionsByListType],
  );

  const flippedUnsalableGridConfig = useMemo(
    () =>
      getFlippedUnsalableGridConfig({ postClosing, getUserName, investorName, optionsByListType }),
    [postClosing, getUserName, investorName, optionsByListType],
  );

  const safeDocumentRecords = useMemo(() => documentRecords ?? [], [documentRecords]);

  const formatDate = useCallback(
    date => (date ? dayjs(date).format(COMMON_REQUEST_DATE_FORMAT) : null),
    [],
  );

  const buildDocumentRecordsPayload = useCallback(
    records =>
      records.map(({ key, finalCdRecordingAmount, receivedDate, toInvestorDate, ...rest }) => ({
        ...rest,
        ...(receivedDate && { receivedDate: formatDate(receivedDate) }),
        ...(toInvestorDate && { toInvestorDate: formatDate(toInvestorDate) }),
        finalCDRecordingAmount: finalCdRecordingAmount,
      })),
    [formatDate],
  );

  const handleSaveDocumentRecord = useCallback(
    (recordData, existingKey) => {
      setIsLoading(true);

      const updatedRecords = existingKey
        ? safeDocumentRecords.map(record =>
            record.key === existingKey ? { ...record, ...recordData } : record,
          )
        : [...safeDocumentRecords, recordData];

      const valuesToSend = buildPostClosingPayload({
        loanId,
        postClosing,
        investorConditions: postClosing?.investorConditions,
        preInvestorConditions: postClosing?.preInvestorConditions,
        closingDocumentRecords: buildDocumentRecordsPayload(updatedRecords),
        tabId: LoanApplicationTabId.CLOSING,
      });

      dispatch(editPostClosingAction(valuesToSend))
        .then(() => {
          dispatch(
            showMessageAction({
              message: existingKey
                ? 'Document record updated successfully'
                : 'Document record added successfully',
              variant: 'success',
            }),
          );
        })
        .catch(error => {
          logger.error(error);
          dispatch(
            showMessageAction({ message: 'Failed to save document record', variant: 'error' }),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, buildDocumentRecordsPayload, loanId, postClosing, safeDocumentRecords, setIsLoading],
  );

  const handleDeleteDocumentRecord = useCallback(
    recordKey => {
      setIsLoading(true);

      const updatedRecords = safeDocumentRecords.filter(record => record.key !== recordKey);

      const valuesToSend = buildPostClosingPayload({
        loanId,
        postClosing,
        investorConditions: postClosing?.investorConditions,
        preInvestorConditions: postClosing?.preInvestorConditions,
        closingDocumentRecords: buildDocumentRecordsPayload(updatedRecords),
        tabId: LoanApplicationTabId.CLOSING,
      });

      dispatch(editPostClosingAction(valuesToSend))
        .then(() => {
          dispatch(
            showMessageAction({
              message: 'Document record deleted successfully',
              variant: 'success',
            }),
          );
        })
        .catch(error => {
          logger.error(error);
          dispatch(
            showMessageAction({ message: 'Failed to delete document record', variant: 'error' }),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, buildDocumentRecordsPayload, loanId, postClosing, safeDocumentRecords, setIsLoading],
  );

  const saveInvestorConditions = useCallback(
    (updatedConditions, successMessage) => {
      setIsLoading(true);

      const valuesToSend = buildPostClosingPayload({
        loanId,
        postClosing,
        investorConditions: updatedConditions,
        preInvestorConditions: postClosing?.preInvestorConditions,
        closingDocumentRecords: buildDocumentRecordsPayload(safeDocumentRecords),
        tabId: LoanApplicationTabId.CLOSING,
      });

      dispatch(editPostClosingAction(valuesToSend))
        .then(() => {
          dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
        })
        .catch(error => {
          logger.error(error);
          dispatch(
            showMessageAction({ message: 'Failed to save investor condition', variant: 'error' }),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, buildDocumentRecordsPayload, loanId, postClosing, safeDocumentRecords, setIsLoading],
  );

  const handleSaveCondition = useCallback(
    (conditionData, existingId) => {
      const currentConditions = postClosing?.investorConditions ?? [];

      const updatedConditions = existingId
        ? currentConditions.map(condition =>
            condition.id === existingId ? { ...condition, ...conditionData } : condition,
          )
        : [
            ...currentConditions,
            {
              id: crypto.randomUUID(),
              ...conditionData,
            },
          ];

      saveInvestorConditions(
        updatedConditions,
        existingId ? 'Condition updated successfully' : 'Condition added successfully',
      );
    },
    [postClosing?.investorConditions, saveInvestorConditions],
  );

  const handleDeleteCondition = useCallback(
    conditionId => {
      const currentConditions = postClosing?.investorConditions ?? [];
      const updatedConditions = currentConditions.filter(condition => condition.id !== conditionId);

      saveInvestorConditions(updatedConditions, 'Condition deleted successfully');
    },
    [postClosing?.investorConditions, saveInvestorConditions],
  );

  const handleMarkConditionResponded = useCallback(
    updatedConditions => {
      saveInvestorConditions(updatedConditions, 'Condition marked as responded');
    },
    [saveInvestorConditions],
  );

  const handleMarkConditionCleared = useCallback(
    updatedConditions => {
      saveInvestorConditions(updatedConditions, 'Condition marked as cleared');
    },
    [saveInvestorConditions],
  );

  const handleBulkAddConditions = useCallback(
    newConditions => {
      const currentConditions = postClosing?.investorConditions ?? [];
      const updatedConditions = [...currentConditions, ...newConditions];

      saveInvestorConditions(updatedConditions, 'Conditions added successfully');
    },
    [postClosing?.investorConditions, saveInvestorConditions],
  );

  const savePreInvestorConditions = useCallback(
    (updatedConditions, successMessage) => {
      setIsLoading(true);

      const valuesToSend = buildPostClosingPayload({
        loanId,
        postClosing,
        investorConditions: postClosing?.investorConditions,
        preInvestorConditions: updatedConditions,
        closingDocumentRecords: buildDocumentRecordsPayload(safeDocumentRecords),
        tabId: LoanApplicationTabId.CLOSING,
      });

      dispatch(editPostClosingAction(valuesToSend))
        .then(() => {
          dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
        })
        .catch(error => {
          logger.error(error);
          dispatch(
            showMessageAction({
              message: 'Failed to save pre-investor condition',
              variant: 'error',
            }),
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch, buildDocumentRecordsPayload, loanId, postClosing, safeDocumentRecords, setIsLoading],
  );

  const handleSavePreCondition = useCallback(
    (conditionData, existingId) => {
      const currentConditions = postClosing?.preInvestorConditions ?? [];

      const updatedConditions = existingId
        ? currentConditions.map(condition =>
            condition.id === existingId ? { ...condition, ...conditionData } : condition,
          )
        : [
            ...currentConditions,
            {
              id: crypto.randomUUID(),
              ...conditionData,
            },
          ];

      savePreInvestorConditions(
        updatedConditions,
        existingId ? 'Condition updated successfully' : 'Condition added successfully',
      );
    },
    [postClosing?.preInvestorConditions, savePreInvestorConditions],
  );

  const handleDeletePreCondition = useCallback(
    conditionId => {
      const currentConditions = postClosing?.preInvestorConditions ?? [];
      const updatedConditions = currentConditions.filter(condition => condition.id !== conditionId);

      savePreInvestorConditions(updatedConditions, 'Condition deleted successfully');
    },
    [postClosing?.preInvestorConditions, savePreInvestorConditions],
  );

  const handleMarkPreConditionResponded = useCallback(
    updatedConditions => {
      savePreInvestorConditions(updatedConditions, 'Condition marked as responded');
    },
    [savePreInvestorConditions],
  );

  const handleMarkPreConditionCleared = useCallback(
    updatedConditions => {
      savePreInvestorConditions(updatedConditions, 'Condition marked as cleared');
    },
    [savePreInvestorConditions],
  );

  const handleBulkAddPreConditions = useCallback(
    newConditions => {
      const currentConditions = postClosing?.preInvestorConditions ?? [];
      const updatedConditions = [...currentConditions, ...newConditions];

      savePreInvestorConditions(updatedConditions, 'Conditions added successfully');
    },
    [postClosing?.preInvestorConditions, savePreInvestorConditions],
  );

  const openDocumentRecordModal = useCallback(
    (documentRecord = null) => {
      const record = documentRecord?.key ? documentRecord : null;
      dispatch(
        openDialogAction({
          header: record ? 'Edit Document Record' : 'Add Document Record',
          size: ModalSize.LARGE,
          body: dialogProps => (
            <DocumentRecordModal
              documentRecord={record}
              onSave={handleSaveDocumentRecord}
              {...dialogProps}
            />
          ),
        }),
      );
    },
    [dispatch, handleSaveDocumentRecord],
  );

  const renderDocumentActions = useCallback(
    row => (
      <DocumentRecordActions
        isEditAllowed={isEditAllowed}
        row={row}
        onDelete={handleDeleteDocumentRecord}
        onEdit={openDocumentRecordModal}
      />
    ),
    [isEditAllowed, openDocumentRecordModal, handleDeleteDocumentRecord],
  );

  const documentsTableConfig = useMemo(
    () => ({
      ...getDocumentsTableConfig({ isUnsecured }),
      actions: {
        label: '',
        width: '80px',
        content: renderDocumentActions,
      },
    }),
    [renderDocumentActions, isUnsecured],
  );

  const handleEditClick = useCallback(
    (sectionHref, onClearAnchorElement) => () => {
      onClearAnchorElement();
      const path = LoansPaths.applicationManagement.generatePath({
        applicationId,
        tab,
        subtab: sectionHref,
      });
      history.push(path);
    },
    [applicationId, tab, history],
  );

  const getSummaryMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Post Closing Summary',
        onClick: handleEditClick(SECTION_HREF_MAP.INVESTOR, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  const getFinancialMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Financial Details',
        onClick: handleEditClick(SECTION_HREF_MAP.ACCOUNTING, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  const getFinalDocumentsMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Final Documents',
        onClick: handleEditClick(SECTION_HREF_MAP.FINAL_DOCUMENTS, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  const getShipperMilestoneMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Shipper Milestone',
        onClick: handleEditClick(SECTION_HREF_MAP.SHIPPER_MILESTONE, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  const getMersMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit MERS',
        onClick: handleEditClick(SECTION_HREF_MAP.MERS, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
        'data-testid': PostClosingTestIds.EDIT_MERS_BUTTON,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  const getFlippedUnsalableMenuItems = useCallback(
    onClearAnchorElement => [
      {
        text: 'Edit Flipped / Unsalable',
        onClick: handleEditClick(SECTION_HREF_MAP.FLIPPED_UNSALABLE, onClearAnchorElement),
        icon: <EditPenIcon size={IconSize[20]} />,
        disabled: !isEditAllowed,
        'data-testid': PostClosingTestIds.EDIT_FLIPPED_UNSALABLE_BUTTON,
      },
    ],
    [handleEditClick, isEditAllowed],
  );

  if (!isViewAllowed) {
    return null;
  }

  return (
    <>
      <ErrorHandlingLayout isError={isError} onClick={fetchData}>
        <Box sx={wrapperStyles}>
          <PageBlock title="Post Closing Overview">
            <PostClosingOverview isLoading={isLoading} overviewConfig={overviewConfig} />
          </PageBlock>

          <PageBlock
            isMenuOpened={!!summaryPopover.anchorEl}
            title="Investor & Servicer Details"
            withAction={isEditAllowed}
            onMenuClick={summaryPopover.onSetAnchorElement}
          >
            <InvestorServicerSummary
              identifiersConfig={loanIdentifiersConfig}
              investorConfig={investorSummaryConfig}
              isLoading={isLoading}
              servicerConfig={servicerSummaryConfig}
            />
            <MenuPopoverCDS
              anchorEl={summaryPopover.anchorEl}
              items={getSummaryMenuItems(summaryPopover.onClearAnchorElement)}
              onClose={summaryPopover.onClearAnchorElement}
            />
          </PageBlock>

          {!isUnsecured && (
            <PageBlock
              isMenuOpened={!!mersPopover.anchorEl}
              title="MERS"
              withAction={isEditAllowed}
              onMenuClick={mersPopover.onSetAnchorElement}
            >
              <FinalDocumentsSection config={mersGridConfig} isLoading={isLoading} />
              <MenuPopoverCDS
                anchorEl={mersPopover.anchorEl}
                items={getMersMenuItems(mersPopover.onClearAnchorElement)}
                onClose={mersPopover.onClearAnchorElement}
              />
            </PageBlock>
          )}

          <PageBlock title="Investor Conditions">
            <InvestorConditionsTable
              conditions={postClosing?.investorConditions}
              isEditAllowed={isEditAllowed}
              isLoading={isLoading}
              onBulkAddConditions={handleBulkAddConditions}
              onDeleteCondition={handleDeleteCondition}
              onMarkCleared={handleMarkConditionCleared}
              onMarkResponded={handleMarkConditionResponded}
              onSaveCondition={handleSaveCondition}
            />
          </PageBlock>

          <PageBlock title="Pre-Investor Conditions">
            <PreInvestorConditionsTable
              conditions={postClosing?.preInvestorConditions}
              isEditAllowed={isEditAllowed}
              isLoading={isLoading}
              onBulkAddConditions={handleBulkAddPreConditions}
              onDeleteCondition={handleDeletePreCondition}
              onMarkCleared={handleMarkPreConditionCleared}
              onMarkResponded={handleMarkPreConditionResponded}
              onSaveCondition={handleSavePreCondition}
            />
          </PageBlock>

          <PageBlock
            isMenuOpened={!!finalDocumentsPopover.anchorEl}
            title="Final Documents"
            withAction={isEditAllowed}
            onMenuClick={finalDocumentsPopover.onSetAnchorElement}
          >
            <FinalDocumentsSection config={finalDocumentsGridConfig} isLoading={isLoading} />
            <MenuPopoverCDS
              anchorEl={finalDocumentsPopover.anchorEl}
              items={getFinalDocumentsMenuItems(finalDocumentsPopover.onClearAnchorElement)}
              onClose={finalDocumentsPopover.onClearAnchorElement}
            />
          </PageBlock>

          <PageBlock
            isMenuOpened={!!shipperMilestonePopover.anchorEl}
            title="Shipper Milestone"
            withAction={isEditAllowed}
            onMenuClick={shipperMilestonePopover.onSetAnchorElement}
          >
            <ShipperMilestoneSection config={shipperMilestoneGridConfig} isLoading={isLoading} />
            <MenuPopoverCDS
              anchorEl={shipperMilestonePopover.anchorEl}
              items={getShipperMilestoneMenuItems(shipperMilestonePopover.onClearAnchorElement)}
              onClose={shipperMilestonePopover.onClearAnchorElement}
            />
          </PageBlock>

          <PageBlock
            isMenuOpened={!!flippedUnsalablePopover.anchorEl}
            title="Flipped / Unsalable"
            withAction={isEditAllowed}
            onMenuClick={flippedUnsalablePopover.onSetAnchorElement}
          >
            <FinalDocumentsSection config={flippedUnsalableGridConfig} isLoading={isLoading} />
            <MenuPopoverCDS
              anchorEl={flippedUnsalablePopover.anchorEl}
              items={getFlippedUnsalableMenuItems(flippedUnsalablePopover.onClearAnchorElement)}
              onClose={flippedUnsalablePopover.onClearAnchorElement}
            />
          </PageBlock>

          <PageBlock
            isMenuOpened={!!financialPopover.anchorEl}
            title="Financial Details"
            withAction={isEditAllowed}
            onMenuClick={financialPopover.onSetAnchorElement}
          >
            <FinancialDetails
              accountingConfig={accountingGridConfig}
              isLoading={isLoading}
              paymentConfig={paymentGridConfig}
              shippingConfig={shippingGridConfig}
            />
            <MenuPopoverCDS
              anchorEl={financialPopover.anchorEl}
              items={getFinancialMenuItems(financialPopover.onClearAnchorElement)}
              onClose={financialPopover.onClearAnchorElement}
            />
          </PageBlock>

          <PageBlock title="Document Records">
            {isEditAllowed && (
              <Box sx={addDocumentButtonWrapperStyles}>
                <Button
                  data-testid={PostClosingTestIds.ADD_DOCUMENT_BUTTON}
                  size="small"
                  startIcon={<AddBoxIcon size={IconSize[16]} />}
                  onClick={openDocumentRecordModal}
                >
                  Add Document
                </Button>
              </Box>
            )}
            <TableCDS
              withDivider
              withNoAvailableMessage
              config={documentsTableConfig}
              data={safeDocumentRecords}
              isLoading={isLoading}
              skeletonRows={3}
            />
          </PageBlock>
        </Box>
      </ErrorHandlingLayout>

      <EditDrawer isLoadFailed={isError} onRetry={fetchData} />
    </>
  );
};

PostClosing.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(PostClosing);
